import { useEffect } from "react";
import { Modal, message } from "antd";
import db from "../database";
import useSessionStore from "../stores/useSessionStore";
import { tracePreparedBill } from "./preparedBillRecovery";
import { getOpenPreparedBillsForTillSession, cancelPreparedBillsByIds } from "./preparedBillTableLock";
import { isPastBusinessDay } from "./tillBusinessDate";

// BUG-2636: "keep the bill open" on a past business day is asked once per till session, not
// again on each Close Till screen (/close-till → /close-till-all → /cash-to-keep).
const keptOpenForSession = new Set();

/** Test hook: forget the "keep open" answers. */
export const resetPreparedBillCloseTillCheck = () => keptOpenForSession.clear();

const ask = (config) =>
  new Promise((resolve) =>
    Modal.confirm({
      closable: false,
      maskClosable: false,
      ...config,
      onOk: () => resolve("ok"),
      onCancel: () => resolve("cancel"),
    })
  );

const readPastBusinessDay = async () => {
  try {
    const open = await db.tillEvents.where("tillStatus").equals("open").toArray();
    return open.length > 0 && isPastBusinessDay(open[0]);
  } catch {
    return false;
  }
};

/**
 * BUG-2425 / TIC-11390 round 4 / BUG-2636: prepared bills of this till session never paid or
 * cancelled stay completed and un-invoiced in CW. The check used to run only on the restaurant
 * Close Till button (FBLeftSidebar); the next-day redirect, Unlink Till and the other paths open
 * the Close Till screens directly, so those screens run it too (onCloseTillScreen).
 *
 * Returns "none" (no open bill: go on), "back" (leave Close Till), "cancelled" (bills cancelled:
 * leave, so the cancels sync before the till closes), "cancelled-past-day" (cancelled, stay on the
 * screen) or "kept" (past business day on a Close Till screen: close with the bill left open).
 * On a past business day PrivateRoute sends a cashier straight back to /close-till, so "Go back"
 * would loop: there the choices are Cancel or close keeping the bill (support settles it).
 */
export const confirmOpenPreparedBillsBeforeCloseTill = async ({ flow = "close-till", onCloseTillScreen = false, tillSessionId } = {}) => {
  const sessionId = tillSessionId || useSessionStore.getState().tillSession?.tillSessionId;
  if (!sessionId || keptOpenForSession.has(sessionId)) return "none";
  const openBills = await getOpenPreparedBillsForTillSession(sessionId);
  if (openBills.length === 0) return "none";

  const pastDay = onCloseTillScreen && (await readPastBusinessDay());
  const one = openBills.length === 1;
  tracePreparedBill("close-till-open-prepared-bills", { flow, pastDay, bills: openBills.map((b) => ({ sOrderID: b.sOrderID, documentno: b.documentno, tableNo: b.tableNo, total: b.total })) });
  const billList = openBills.map((b) => `${b.documentno}${b.tableNo ? ` (${b.tableNo})` : ""} ₹${b.total}`).join(", ");
  const cancelLabel = one ? "Cancel this bill" : "Cancel these bills";

  const choice = pastDay
    ? await ask({
        title: "Prepared bills not settled",
        content: `${billList} ${one ? "was" : "were"} prepared but never paid, and this till session is from an earlier business day. Cancel ${one ? "it" : "them"} only if the customer did not pay. If ${one ? "it was" : "they were"} paid, close the till keeping ${one ? "it" : "them"} open and ask support to settle ${one ? "it" : "them"} in Back Office.`,
        okText: one ? "Close till, keep bill open" : "Close till, keep bills open",
        cancelText: cancelLabel,
        cancelButtonProps: { danger: true },
      })
    : await ask({
        title: "Prepared bills not settled",
        content: `${billList} ${one ? "was" : "were"} prepared but never paid. Settle ${one ? "it" : "them"} with Pay Bill before closing the till. Cancel only a bill the customer did not pay.`,
        okText: "Go back",
        cancelText: cancelLabel,
        cancelButtonProps: { danger: true },
      });

  if (choice === "cancel") {
    const confirmed =
      (await ask({
        title: "Cancel unpaid prepared bills?",
        content: `${billList} will be cancelled in CW and will NOT be invoiced. This cannot be undone.`,
        okText: "Yes, cancel",
        okButtonProps: { danger: true },
        cancelText: "No",
        closable: true,
      })) === "ok";
    if (confirmed) {
      const cancelled = await cancelPreparedBillsByIds({ sOrderIDs: openBills.map((b) => b.sOrderID), flow });
      message.info(
        pastDay
          ? `${cancelled.length} prepared bill${cancelled.length === 1 ? "" : "s"} cancelled. ${cancelled.length === 1 ? "It is" : "They are"} sent to CW with the next sync.`
          : `${cancelled.length} prepared bill${cancelled.length === 1 ? "" : "s"} cancelled. Press Close Till again once they have synced.`
      );
      tracePreparedBill("close-till-open-prepared-bills-cancelled", { flow, pastDay, count: openBills.length });
      return pastDay ? "cancelled-past-day" : "cancelled";
    }
    if (pastDay) return confirmOpenPreparedBillsBeforeCloseTill({ flow, onCloseTillScreen, tillSessionId: sessionId });
    tracePreparedBill("close-till-open-prepared-bills-went-back", { flow, count: openBills.length });
    return "back";
  }
  if (pastDay) {
    keptOpenForSession.add(sessionId);
    message.warning(`The till will close with ${billList} still open. Ask support to settle ${one ? "it" : "them"} in Back Office.`);
    tracePreparedBill("close-till-open-prepared-bills-kept-open", { flow, count: openBills.length, bills: openBills.map((b) => b.documentno) });
    return "kept";
  }
  tracePreparedBill("close-till-open-prepared-bills-went-back", { flow, count: openBills.length });
  return "back";
};

/**
 * BUG-2636: Close Till screens run the check once on mount. "back" / "cancelled" leave the
 * screen (closeTillBackPath: /till when Close Till was started from the till screen).
 */
export const usePreparedBillCloseTillCheck = (history, flow, backPath) => {
  useEffect(() => {
    let alive = true;
    confirmOpenPreparedBillsBeforeCloseTill({ flow, onCloseTillScreen: true }).then((result) => {
      if (alive && (result === "back" || result === "cancelled")) history.push(backPath);
    });
    return () => {
      alive = false;
    };
  }, [history, flow, backPath]);
};
